import { useState, useEffect } from "react";
import CourseViewer from "./courseviewer";

/* ─── Deterministic star positions (avoids Math.random on every render) ─── */
const STARS = Array.from({ length: 140 }, (_, i) => ({
  w: (((i * 7 + 13) % 19) / 10 + 0.8).toFixed(1),
  h: (((i * 11 + 7)  % 17) / 10 + 0.8).toFixed(1),
  top:  ((i * 13 + 5)  % 100).toFixed(2),
  left: ((i * 17 + 3)  % 100).toFixed(2),
}));

const Stars = ({ count = 80 }) => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
    {STARS.slice(0, count).map((s, i) => (
      <div
        key={i}
        className="absolute rounded-full bg-white"
        style={{ width: s.w + 'px', height: s.h + 'px', top: s.top + '%', left: s.left + '%', opacity: 0.35 }}
      />
    ))}
  </div>
);

/* ─── Reusable Step Card ─────────────────────────────────────────────────── */
const StepCard = ({ num, icon, title, desc }) => (
  <div
    className="relative rounded-2xl p-5 sm:p-6 group transition-all duration-300 hover:-translate-y-1"
    style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 4px 24px rgba(0,0,0,0.3)' }}
  >
    <div className="absolute top-0 left-6 right-6 h-0.5 rounded-full bg-orange-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
    <div className="flex items-center justify-between mb-4">
      <span className="text-xs font-bold tracking-widest text-orange-500">{num}</span>
      <div className="w-9 h-9 rounded-xl flex items-center justify-center text-orange-400" style={{ backgroundColor: 'rgba(234,88,12,0.15)' }}>
        {icon}
      </div>
    </div>
    <div className="w-full h-px mb-3" style={{ background: 'linear-gradient(to right, rgba(234,88,12,0.6), transparent)' }} />
    <h3 className="text-white font-bold text-sm sm:text-base mb-1 sm:mb-2">{title}</h3>
    <p className="text-gray-400 text-xs sm:text-sm leading-relaxed">{desc}</p>
  </div>
);

/* ─── SVG Icons ──────────────────────────────────────────────────────────── */
const PhoneIcon   = () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" /></svg>;
const DocIcon     = () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>;
const ClipIcon    = () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z" /></svg>;
const PenIcon     = () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" /></svg>;
const RocketIcon  = () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84" /></svg>;
const ChatIcon    = () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M2.25 12.76c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.076-4.076a1.526 1.526 0 011.037-.443 48.282 48.282 0 005.68-.494c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" /></svg>;
const ArrowIcon   = () => <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" /></svg>;

/* ═══════════════════════════════════════════════════════════════════════════
   COLOUR TOKENS
═══════════════════════════════════════════════════════════════════════════ */
const C = {
  dark:  '#1a0800',
  mid:   '#3d1a00',
  deep:  '#0f0400',
  card:  '#2a1200',
};

/* ─── Your Courses Card ─────────────────────────────────────────────────── */
const CourseCard = ({ course, baseUrl, onClick }) => {
  const pct = course.percentage || 0;
  const fallbackImg = `${baseUrl}/public/Easylearn/images/easylearn-logo.png`;
  return (
    <div
      onClick={onClick}
      className="rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl"
      style={{
        backgroundColor: '#2a1200',
        border: '1px solid rgba(234,88,12,0.25)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
        minWidth: '220px',
        maxWidth: '260px',
        flex: '0 0 auto',
      }}
    >
      <div className="relative" style={{ height: '150px', overflow: 'hidden' }}>
        <img
          src={course.course_image || fallbackImg}
          alt={course.course_name}
          className="w-full h-full object-cover"
          onError={e => { e.target.src = fallbackImg; }}
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(26,8,0,0.7) 0%, transparent 60%)' }} />
      </div>
      <div className="p-4">
        <h3 className="text-white font-bold text-sm mb-1 leading-snug" style={{ minHeight: '36px' }}>
          {course.course_name}
        </h3>
        {course.partner_name && (
          <p className="text-orange-400 text-xs mb-3 capitalize">{course.partner_name}</p>
        )}
        <div className="mb-1">
          <div className="flex justify-between items-center mb-1">
            <span className="text-gray-400 text-xs">Progress</span>
            <span className="text-orange-400 text-xs font-bold">{pct}%</span>
          </div>
          <div className="w-full rounded-full" style={{ height: '5px', backgroundColor: 'rgba(255,255,255,0.1)' }}>
            <div
              className="rounded-full"
              style={{
                width: `${pct}%`,
                height: '5px',
                background: pct === 100 ? 'linear-gradient(90deg,#22c55e,#16a34a)' : 'linear-gradient(90deg,#ea580c,#f97316)',
                transition: 'width 0.5s ease',
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════════════════════ */
export default function ISFAthena() {
  const [openFaq, setOpenFaq] = useState(null);
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(false);

  // ── Course Viewer state ────────────────────────────────────────────────
  const [selectedCourse, setSelectedCourse] = useState(null); // which course card was clicked
  const [courseTopics,   setCourseTopics]   = useState([]);   // flat topic list for sidebar
  const [activeTopic,    setActiveTopic]    = useState(null); // currently selected topic
  const [courseData,     setCourseData]     = useState(null); // data passed to CourseViewer
  const [viewerLoading,  setViewerLoading]  = useState(false);
  const [courseName,     setCourseName]     = useState('');
  const [courseImg,      setCourseImg]      = useState('');

  const baseUrl = 'https://management.skillmaster.net.in';

  // ── Fetch enrolled AI courses — direct POST with hardcoded student id ──────
  // Same pattern as Book.js fetchAndAddBooks (no login needed)
  useEffect(() => {
    const fetchCourses = async () => {
      setCoursesLoading(true);
      try {
        const res = await fetch(
          `${baseUrl}/AI_Controller/enrolled_aicourse_details_getdata`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({ id: '41' }), // schoolstudent1 - el_accounts id
          }
        );
        const json = await res.json();
        if (json.data && json.data !== 'No Data Found' && Array.isArray(json.data)) {
          const normalized = json.data.map(c => ({
            ...c,
            course_image: c.course_img || c.course_image || '',
            partner_name: c.partner_name || 'AI Powered Learning',
            type: c.type || 'AI',
          }));
          setEnrolledCourses(normalized);
        }
      } catch (err) {
        console.warn('[YourCourses] fetch error:', err.message);
      } finally {
        setCoursesLoading(false);
      }
    };
    fetchCourses();
  }, [baseUrl]);

  // ── Flatten get_ai_topic_name topics object → flat array ──────────────────
  const flattenTopics = (topicsObj) => {
    const list = [];
    if (!topicsObj || typeof topicsObj !== 'object') return list;
    Object.entries(topicsObj).forEach(([topicName, topicData]) => {
      if (!topicData) return;
      if (topicData.unique_id) {
        list.push({ modal_name: topicName, unique_id: topicData.unique_id, level: 0 });
        return;
      }
      if (topicData.sub_topic) {
        list.push({ modal_name: topicName, unique_id: null, level: 0, isHeader: true });
        Object.entries(topicData.sub_topic).forEach(([subName, subData]) => {
          if (!subData) return;
          if (subData.unique_id) {
            list.push({ modal_name: subName, unique_id: subData.unique_id, level: 1 });
            return;
          }
          if (subData.chapter) {
            list.push({ modal_name: subName, unique_id: null, level: 1, isHeader: true });
            Object.entries(subData.chapter).forEach(([chName, chData]) => {
              if (chData?.unique_id)
                list.push({ modal_name: chName, unique_id: chData.unique_id, level: 2 });
            });
          }
        });
      }
    });
    return list;
  };

  // ── Map el_ai_topics DB row → CourseViewer courseData shape ───────────────
  // DB cols: topic_docs, topic_ppt, topic_book, reference_book, website, social_media
  const mapTopicToViewerData = (api, topicLabel, cName) => ({
    modal_name:     topicLabel || api?.topic_name || '',
    bookTitle:      cName      || courseName      || '',
    description:    api?.topic_description        || '',
    totalPages:     Number(api?.total_pages)      || 52,
    document:       api?.topic_docs               || null,
    ppt:            api?.topic_ppt                || null,
    book:           api?.topic_book               || null,
    reference_book: api?.reference_book           || null,
    website:        api?.website                  || null,
    social_media:   api?.social_media             || null,
  });

  // ── Load topic document from el_ai_topics ─────────────────────────────────
  const loadTopicDoc = async (topic, cName) => {
    if (!topic?.unique_id) return;
    setActiveTopic(topic);
    setViewerLoading(true);
    setCourseData(null);
    try {
      const res = await fetch(`${baseUrl}/AI_Controller/get_ai_topic_by_id`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body:    new URLSearchParams({ id: topic.unique_id }),
      });
      const json = await res.json();
      setCourseData(mapTopicToViewerData(json?.data || {}, topic.modal_name, cName));
    } catch (err) {
      console.warn('[CourseViewer] topic load error:', err.message);
      setCourseData(mapTopicToViewerData({}, topic.modal_name, cName));
    } finally {
      setViewerLoading(false);
    }
  };

  // ── Card click → fetch topics → open CourseViewer ─────────────────────────
  const handleCourseClick = async (course) => {
    setSelectedCourse(course);
    setViewerLoading(true);
    setCourseTopics([]);
    setCourseData(null);
    try {
      const res = await fetch(`${baseUrl}/AI_Controller/get_ai_topic_name`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body:    new URLSearchParams({ id: course.unique_id, account_id: '41' }),
      });
      const json = await res.json();
      if (!json?.data || json.data === 'False') { setViewerLoading(false); return; }

      const d = json.data;
      setCourseName(d.course_name  || course.course_name || '');
      setCourseImg(d.course_image  || course.course_image || '');

      const flat = flattenTopics(d.topics || {});
      setCourseTopics(flat);

      // Auto-load first topic
      const first = flat.find(t => t.unique_id);
      if (first) await loadTopicDoc(first, d.course_name || course.course_name);
    } catch (err) {
      console.warn('[CourseViewer] topics load error:', err.message);
    } finally {
      setViewerLoading(false);
    }
  };

  // ── Inline CourseViewer (when a course card is clicked) ───────────────────
  if (selectedCourse) {
    return (
      <div className="w-full h-screen flex flex-col overflow-hidden bg-gray-950">
        {/* Loading overlay while fetching topics/doc */}
        {viewerLoading && (
          <div style={{ position:'fixed', inset:0, backgroundColor:'rgba(0,0,0,0.7)',
            display:'flex', alignItems:'center', justifyContent:'center', zIndex:50 }}>
            <div style={{ width:48, height:48, border:'4px solid #ea580c',
              borderTopColor:'transparent', borderRadius:'50%',
              animation:'spin 0.8s linear infinite' }} />
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          </div>
        )}
        {courseData && (
          <CourseViewer
            courseData={courseData}
            onBack={() => { setSelectedCourse(null); setCourseData(null); setCourseTopics([]); }}
            showHeader={false}
            showSidebar={true}
            showBottomControls={true}
            productName={courseName}
            siblingsModals={courseTopics.filter(t => t.unique_id)}
            onSelectModel={(topic) => loadTopicDoc(topic, courseName)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="font-sans text-white" style={{ backgroundColor: C.dark }}>

      {/* ══════════════════════════════════════════════════════════
          0. YOUR COURSES
      ══════════════════════════════════════════════════════════ */}
      {(coursesLoading || enrolledCourses.length > 0) && (
        <section
          className="relative px-4 sm:px-8 lg:px-24 py-10 sm:py-12 overflow-hidden"
          style={{ backgroundColor: C.deep, borderBottom: '1px solid rgba(234,88,12,0.15)' }}
        >
          <div className="max-w-7xl mx-auto">
            {/* Section Header */}
            <div className="flex items-center gap-3 mb-6 mt-[30px] completed">
              <div className="w-1 h-8 rounded-full" style={{ background: 'linear-gradient(180deg,#ea580c,#c2410c)' }} />
              <h2 className="text-xl sm:text-2xl font-bold text-white">Management</h2>
              {!coursesLoading && (
                <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-bold text-orange-400" style={{ backgroundColor: 'rgba(234,88,12,0.15)', border: '1px solid rgba(234,88,12,0.3)' }}>
                  {enrolledCourses.length}
                </span>
              )}
            </div>

            {/* Loading Skeleton */}
            {coursesLoading ? (
              <div className="flex gap-4 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none' }}>
                {[1,2,3,4].map(i => (
                  <div
                    key={i}
                    className="rounded-2xl flex-shrink-0"
                    style={{ width: '240px', height: '220px', backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(234,88,12,0.1)', animation: 'pulse 1.5s infinite' }}
                  />
                ))}
              </div>
            ) : (
              /* Course Cards Horizontal Scroll */
              <div
                className="flex gap-5 overflow-x-auto pb-3"
                style={{ scrollbarWidth: 'thin', scrollbarColor: '#ea580c #1a0800' }}
              >
                {enrolledCourses.map((course) => (
                  <CourseCard
                    key={course.unique_id}
                    course={course}
                    baseUrl={baseUrl}
                    onClick={() => handleCourseClick(course)}
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* ══════════════════════════════════════════════════════════
          1. HERO
      ══════════════════════════════════════════════════════════ */}
      <section
        className="relative flex items-center px-4 sm:px-8 lg:px-24 py-16 sm:py-20 lg:py-28 overflow-hidden"
        style={{ backgroundColor: C.dark }}
      >
        <Stars count={120} />
        <div className="max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center relative z-10">

          {/* Text */}
          <div className="space-y-5 sm:space-y-6 text-center lg:text-left">
            {/* <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-orange-500/40 text-orange-400 text-xs font-bold tracking-widest"
              style={{ backgroundColor: 'rgba(234,88,12,0.1)' }}>
              <span className="w-1.5 h-1.5 rounded-full bg-orange-400 animate-pulse" />
              ISF ATHENA COURSEWARE
            </div> */}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-tight">
              ISF Athena<br className="hidden sm:block" />
              <span className="text-orange-400">Courseware</span>
            </h1>
            <p className="text-gray-300 text-sm sm:text-base lg:text-lg leading-relaxed max-w-lg mx-auto lg:mx-0">
              Empowering learners through structured, scalable, and industry-aligned educational solutions.
            </p>
            <div className="flex flex-wrap gap-3 sm:gap-4 justify-center lg:justify-start">
              <button
                className="px-6 sm:px-8 py-3 text-white font-bold rounded-xl text-sm sm:text-base transition-all duration-300 hover:scale-105 hover:shadow-2xl"
                style={{ background: 'linear-gradient(135deg,#ea580c,#c2410c)', boxShadow: '0 6px 24px rgba(234,88,12,0.35)' }}
              >
                Get Started
              </button>
              <button className="px-6 sm:px-8 py-3 border-2 border-orange-500 text-orange-400 font-bold rounded-xl text-sm sm:text-base hover:bg-orange-500 hover:text-white transition-all duration-300">
                Learn More
              </button>
            </div>
          </div>

          {/* Image – hidden on small screens */}
          <div className="hidden lg:block">
            <div className="rounded-2xl overflow-hidden" style={{ height: '380px' }}>
              <img src="/Assests/home_page_151.png" alt="ISF Athena Courseware" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          2. WHAT IS COURSEWARE?
      ══════════════════════════════════════════════════════════ */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-16 sm:py-20 overflow-hidden" style={{ backgroundColor: C.mid }}>
        <Stars count={60} />
        <div className="max-w-7xl mx-auto relative z-10">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4 leading-tight">What is Courseware?</h2>
          <p className="text-gray-300 text-sm sm:text-base leading-relaxed mb-8 sm:mb-12 max-w-4xl">
            ISF Athena's courseware is a complete instructional package designed to deliver exceptional learning experiences. Our comprehensive solution includes learning modules with defined outcomes, slide decks, learner workbooks, assessment tools, facilitator guides, and online support materials.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8">
            {[
              { icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0118 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" /></svg>, title: 'Complete Learning Modules', desc: 'Structured units with clear learning outcomes, activities, and real-world applications' },
              { icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z" /></svg>, title: 'Assessment Tools', desc: 'Quizzes, assignments, and projects with marking schemes and feedback templates' },
              { icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5" /></svg>, title: 'Teaching Resources', desc: 'Slide decks, facilitator guides, and interactive content for effective delivery' },
              { icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941" /></svg>, title: 'Progress Tracking', desc: 'Dashboard templates and reports to monitor learner outcomes and engagement' },
            ].map((item, idx) => (
              <div key={idx} className="flex items-start gap-4">
                <div className="text-orange-400 flex-shrink-0 mt-0.5 w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(234,88,12,0.15)' }}>
                  {item.icon}
                </div>
                <div>
                  <h3 className="text-white font-bold text-sm sm:text-base mb-1">{item.title}</h3>
                  <p className="text-gray-300 text-xs sm:text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          3. CORE OBJECTIVES
      ══════════════════════════════════════════════════════════ */}
      <section
        className="relative px-4 sm:px-8 lg:px-24 py-16 sm:py-20 overflow-hidden"
        style={{ backgroundImage: "url('/Assests/home_page_152.png')", backgroundSize: 'cover', backgroundPosition: 'center' }}
      >
        <div className="absolute inset-0 bg-black/70 z-0" />
        <div className="max-w-7xl mx-auto relative z-10">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-8 sm:mb-10">Our Core Objectives</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
            {[
              { title: 'Industry Alignment', desc: 'Structured, modular, scalable learning materials aligned with current industry and academic needs' },
              { title: 'Best Practices', desc: 'Incorporating adult learning principles, blended delivery, practical application, and comprehensive assessment' },
              { title: 'Flexibility', desc: 'Customizable products for schools, SMEs, and training providers with easy deployment' },
              { title: 'Quality Consistency', desc: 'Ensuring consistency in quality, branding, and learner journey across all modules' },
            ].map((item, idx) => (
              <div key={idx} className="border border-orange-500/70 rounded-2xl p-5 sm:p-7" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
                <h3 className="text-white font-bold text-base sm:text-lg mb-2">{item.title}</h3>
                <p className="text-gray-300 text-xs sm:text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          4. MODULARITY & SCALABILITY
      ══════════════════════════════════════════════════════════ */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-16 sm:py-20 overflow-hidden" style={{ backgroundColor: C.mid }}>
        <Stars count={40} />
        <div className="max-w-7xl mx-auto relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div className="space-y-5 text-center lg:text-left order-2 lg:order-1">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight">Modularity & Scalability</h2>
            <div className="border border-white/20 rounded-xl p-5 sm:p-6 space-y-3 text-left">
              <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                Each course is broken down into modules and sub-modules, making it easy to scale up from beginner to advanced levels or scale out with related modules and specializations.
              </p>
              <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                Client organizations can pick and choose relevant modules or opt for the full suite, ensuring perfect alignment with their specific learning objectives and organizational needs.
              </p>
            </div>
          </div>
          <div className="flex items-center justify-center order-1 lg:order-2">
            <div className="rounded-2xl overflow-hidden bg-amber-50 p-3 sm:p-4 shadow-2xl w-full max-w-xs sm:max-w-sm lg:max-w-md">
              <img src="/Assests/home_page_153.png" alt="Modularity and Scalability" className="w-full object-contain" style={{ maxHeight: '320px' }} />
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          5. FLEXIBLE DELIVERY MODES
      ══════════════════════════════════════════════════════════ */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-16 sm:py-20 overflow-hidden" style={{ backgroundColor: C.dark }}>
        <Stars count={80} />
        <div className="max-w-7xl mx-auto relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-start">
          <div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-8 sm:mb-10">Flexible Delivery Modes</h2>
            <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 gap-5 mb-5">
              {[
                { num: '1', title: 'Face-to-Face Learning', desc: 'Instructor-led sessions with slide decks, workbooks, and interactive activities for hands-on engagement' },
                { num: '2', title: 'Blended Approach', desc: 'Combining instructor-led sessions with e-learning content for optimal flexibility and effectiveness' },
              ].map((item, idx) => (
                <div key={idx} className="relative border border-orange-500/70 rounded-2xl p-5 pt-8" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center font-bold text-white text-sm">{item.num}</div>
                  <h3 className="text-white font-bold text-sm mb-2">{item.title}</h3>
                  <p className="text-gray-400 text-xs leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
            <div className="relative border border-orange-500/70 rounded-2xl p-5 pt-8 mb-5 sm:mb-6" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center font-bold text-white text-sm">3</div>
              <h3 className="text-white font-bold text-sm mb-2">Fully Online</h3>
              <p className="text-gray-400 text-xs leading-relaxed">Self-paced modules with videos, quizzes, and discussion forums for remote learners</p>
            </div>
            <p className="text-gray-400 text-xs sm:text-sm leading-relaxed">
              This flexibility allows ISF Athena to serve schools, corporate L&D departments, SMEs, and remote learners across diverse contexts and geographies.
            </p>
          </div>
          <div className="rounded-2xl overflow-hidden w-full" style={{ height: '360px' }}>
            <img src="/Assests/home_page_154.png" alt="Flexible Delivery Modes" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          6. REAL-WORLD APPLICATION
      ══════════════════════════════════════════════════════════ */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-16 sm:py-20 overflow-hidden" style={{ backgroundColor: C.mid }}>
        <Stars count={40} />
        <div className="max-w-5xl mx-auto relative z-10">
          <div className="rounded-2xl p-6 sm:p-8 lg:p-10" style={{ backgroundColor: C.card }}>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3">Real-World Application</h2>
            <p className="text-gray-300 text-xs sm:text-sm leading-relaxed mb-6 sm:mb-8 max-w-3xl">
              Each module includes up-to-date case studies, example scenarios, and practical assignments so that learners can immediately apply concepts. This approach enhances retention and transfer of learning to the workplace or academic environment.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 sm:gap-6">
              {[
                { img: '/Assests/home_page_155.png', title: 'Current Case Studies', desc: 'Real-world examples from leading organizations and industries' },
                { img: '/Assests/home_page_156.png', title: 'Practical Assignments', desc: 'Hands-on projects that mirror workplace challenges' },
                { img: '/Assests/home_page_152.png', title: 'Scenario-Based Learning', desc: 'Contextual examples that build critical thinking skills' },
              ].map((item, idx) => (
                <div key={idx}>
                  <div className="rounded-xl overflow-hidden mb-3" style={{ height: '150px' }}>
                    <img src={item.img} alt={item.title} className="w-full h-full object-cover" />
                  </div>
                  <h3 className="text-white font-bold text-sm mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-xs leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          7. ASSESSMENT & CERTIFICATION
      ══════════════════════════════════════════════════════════ */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-16 sm:py-20 overflow-hidden" style={{ backgroundColor: C.dark }}>
        <Stars count={80} />
        <div className="max-w-7xl mx-auto relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-start">
          <div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3">Assessment & Certification</h2>
            <p className="text-gray-400 text-xs sm:text-sm leading-relaxed mb-4">
              To ensure credible learning outcomes, our courseware includes comprehensive assessment tools and certification options that validate learner achievement and skill development.
            </p>
            <div>
              {[
                { num: '01', title: 'Formative Assessments', desc: 'Quizzes and checkpoints throughout the learning journey' },
                { num: '02', title: 'Summative Assessment', desc: 'Final projects and tests to evaluate overall mastery' },
                { num: '03', title: 'Facilitator Support', desc: 'Marking schemes and feedback templates for consistent evaluation' },
                { num: '04', title: 'Certification', desc: 'Certificates of completion or integration with client credential systems' },
              ].map((item, idx) => (
                <div key={idx} className="py-3 sm:py-4 border-b border-white/5 last:border-0">
                  <p className="text-gray-500 text-xs font-semibold mb-1 tracking-widest">{item.num}</p>
                  <div className="w-full h-px mb-2 sm:mb-3" style={{ background: 'linear-gradient(to right,#ea580c,transparent)' }} />
                  <h3 className="text-white font-bold text-sm sm:text-base mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-xs sm:text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden w-full" style={{ height: '420px' }}>
            <img src="/Assests/home_page_157.png" alt="Assessment & Certification" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          8. CUSTOMIZATION & BRANDING
      ══════════════════════════════════════════════════════════ */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-16 sm:py-20 overflow-hidden" style={{ backgroundColor: C.dark }}>
        <Stars count={60} />
        <div className="max-w-5xl mx-auto relative z-10">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-6 sm:mb-8 text-center">Customization & Branding</h2>
          <div className="border border-gray-700/70 rounded-2xl overflow-hidden grid grid-cols-1 lg:grid-cols-2">
            <div className="p-6 sm:p-8 border-b lg:border-b-0 lg:border-r border-gray-700/70">
              <h3 className="text-white font-bold text-base sm:text-lg mb-4 sm:mb-5">What We Customize</h3>
              <ul className="space-y-3">
                {[
                  'Re-branding of slide decks and workbooks with client logo and colors',
                  'Adding organization-specific case studies or data',
                  'Adjusting module order, duration, or learning outcomes',
                  'Tailoring content to suit faculty or employee profiles',
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <span className="text-orange-500 text-sm mt-0.5 flex-shrink-0">•</span>
                    <p className="text-gray-300 text-xs sm:text-sm leading-relaxed">{item}</p>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex flex-col">
              <div className="overflow-hidden" style={{ minHeight: '200px', flex: 1 }}>
                <img src="/Assests/home_page_158.png" alt="Customization & Branding" className="w-full h-full object-cover" />
              </div>
              <div className="p-4 sm:p-5 border-t border-gray-700/70">
                <p className="text-gray-300 text-xs sm:text-sm leading-relaxed">
                  ISF Athena handles template modification and ensures brand consistency throughout all materials, giving clients ownership and alignment with their organizational identity.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          9. ANALYTICS & REPORTING
      ══════════════════════════════════════════════════════════ */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-16 sm:py-20 overflow-hidden" style={{ backgroundColor: C.mid }}>
        <Stars count={60} />
        <div className="max-w-7xl mx-auto relative z-10">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">Analytics & Reporting</h2>
          <p className="text-gray-400 text-sm sm:text-base leading-relaxed mb-12 sm:mb-16 max-w-4xl">
            Courseware includes templates for tracking learner progress, completion rates, quiz scores, time-on-task, and feedback. Clients receive a dashboard summary enabling informed decisions on further learning interventions.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-10 text-center">
            {[
              { stat: '100%', title: 'Progress Tracking', desc: 'Complete visibility into learner completion rates' },
              { stat: '360°', title: 'Performance View', desc: 'Comprehensive assessment of quiz scores and outcomes' },
              { stat: '24/7', title: 'Real-Time Data', desc: 'Continuous monitoring of engagement and time-on-task' },
            ].map((item, idx) => (
              <div key={idx} className="space-y-3">
                <p className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white">{item.stat}</p>
                <h3 className="text-white font-bold text-sm sm:text-base">{item.title}</h3>
                <p className="text-gray-400 text-xs sm:text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          10. TARGET SEGMENTS
      ══════════════════════════════════════════════════════════ */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-16 sm:py-20 overflow-hidden" style={{ backgroundColor: C.dark }}>
        <Stars count={60} />
        <div className="max-w-7xl mx-auto relative z-10">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-8 sm:mb-10">Target Segments</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
            {[
              { icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 21v-8.25M15.75 21v-8.25M8.25 21v-8.25M3 9l9-6 9 6m-1.5 12V10.332A48.36 48.36 0 0012 9.75c-2.551 0-5.056.2-7.5.582V21M3 21h18M12 6.75h.008v.008H12V6.75z" /></svg>, title: 'Schools (K-12)', desc: 'Complete curriculum modules, teacher professional development, and supplementary materials for exam preparation and enrichment' },
              { icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z" /></svg>, title: 'SMEs & Corporate', desc: 'Upskilling modules, onboarding programs, and leadership workshops for business growth' },
              { icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M12 14l9-5-9-5-9 5 9 5z" /><path d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0112 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" /></svg>, title: 'Higher Education', desc: 'Short-term certificate courses, bridge programs, and continuous professional development modules' },
              { icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941" /></svg>, title: 'Public Sector & NGOs', desc: 'Community training modules and training-of-trainer programs for government initiatives' },
            ].map((item, idx) => (
              <div key={idx} className="border border-orange-500/70 rounded-2xl p-5 sm:p-7" style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}>
                <div className="w-9 h-9 bg-orange-500 rounded-full flex items-center justify-center text-white mb-3 sm:mb-4">{item.icon}</div>
                <h3 className="text-orange-400 font-bold text-sm sm:text-base mb-2">{item.title}</h3>
                <p className="text-gray-300 text-xs sm:text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          11. PRODUCTION PROCESS
      ══════════════════════════════════════════════════════════ */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-16 sm:py-20 overflow-hidden" style={{ backgroundColor: C.mid }}>
        <Stars count={60} />
        <div className="max-w-4xl mx-auto relative z-10">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-12 sm:mb-16">Courseware Production Process</h2>

          {/* Mobile: stacked cards */}
          <div className="flex flex-col gap-4 lg:hidden">
            {[
              { num: '1', title: 'Needs Assessment & Briefing', desc: 'Client meeting, learning needs analysis, defining target learners and outcomes' },
              { num: '2', title: 'Design & Storyboarding', desc: 'Module structure planning, storyboard creation, assessment design' },
              { num: '3', title: 'Content Development', desc: 'Slide deck creation, workbook development, video production, case study research' },
              { num: '4', title: 'Review & Quality Assurance', desc: 'Internal peer-review, pilot testing, feedback incorporation' },
              { num: '5', title: 'Finalization & Delivery', desc: 'Packaging materials, client customization, hand-over' },
              { num: '6', title: 'Implementation & Support', desc: 'Trainer briefing, rollout support, post-delivery monitoring' },
              { num: '7', title: 'Evaluation & Update', desc: 'Performance analysis, module updates based on standards' },
            ].map((item, idx) => (
              <div key={idx} className="flex items-start gap-4">
                <div className="w-9 h-9 flex-shrink-0 border-2 border-orange-500 rounded-xl flex items-center justify-center font-bold text-white text-sm" style={{ backgroundColor: C.mid }}>
                  {item.num}
                </div>
                <div>
                  <h3 className="text-white font-bold text-sm mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-xs leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop: zigzag timeline */}
          <div className="hidden lg:block relative">
            <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gray-700 -translate-x-1/2" />
            <div className="space-y-10">
              {[
                { num: '1', title: 'Needs Assessment & Briefing', desc: 'Client meeting, learning needs analysis, defining target learners and outcomes', side: 'left' },
                { num: '2', title: 'Design & Storyboarding', desc: 'Module structure planning, storyboard creation, assessment design', side: 'right' },
                { num: '3', title: 'Content Development', desc: 'Slide deck creation, workbook development, video production, case study research', side: 'left' },
                { num: '4', title: 'Review & Quality Assurance', desc: 'Internal peer-review, pilot testing, feedback incorporation', side: 'right' },
                { num: '5', title: 'Finalization & Delivery', desc: 'Packaging materials, client customization, hand-over', side: 'left' },
                { num: '6', title: 'Implementation & Support', desc: 'Trainer briefing, rollout support, post-delivery monitoring', side: 'right' },
                { num: '7', title: 'Evaluation & Update', desc: 'Performance analysis, module updates based on standards', side: 'left' },
              ].map((item, idx) => (
                <div key={idx} className={`relative flex ${item.side === 'right' ? 'justify-end' : 'justify-start'}`}>
                  <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 w-10 h-10 border-2 border-orange-500 rounded-xl flex items-center justify-center font-bold text-white text-sm" style={{ backgroundColor: C.mid }}>
                    {item.num}
                  </div>
                  <div className={`w-5/12 ${item.side === 'right' ? 'text-left pl-8' : 'text-right pr-8'}`}>
                    <h3 className="text-white font-bold text-sm mb-1">{item.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          12. PRICING & LICENSING
      ══════════════════════════════════════════════════════════ */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-16 sm:py-20 overflow-hidden" style={{ backgroundColor: C.deep }}>
        <Stars count={60} />
        <div className="max-w-7xl mx-auto relative z-10">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-8 sm:mb-10">Pricing & Licensing Models</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 mb-5 sm:mb-6">
            {[
              { title: 'Standard License', desc: 'One-time fee for use by one institution with unlimited learner usage' },
              { title: 'Enterprise License', desc: 'Multi-site deployment with custom branding and dedicated trainer guide' },
              { title: 'Subscription Model', desc: 'Annual fee for ongoing updates, analytics dashboard, and new modules' },
              { title: 'Customization Add-ons', desc: 'Branding, local language versions, additional case studies, e-learning build-out' },
            ].map((item, idx) => (
              <div key={idx} className="border border-orange-500/70 rounded-2xl p-5 sm:p-7" style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}>
                <h3 className="text-orange-400 font-bold text-sm sm:text-base mb-2">{item.title}</h3>
                <p className="text-gray-300 text-xs sm:text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
          <p className="text-gray-400 text-xs sm:text-sm leading-relaxed">
            Clients receive a clear price sheet including base cost plus options for add-ons, ensuring transparency and flexibility in investment planning.
          </p>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          13. COMPETITIVE ADVANTAGE
      ══════════════════════════════════════════════════════════ */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-16 sm:py-20 overflow-hidden" style={{ backgroundColor: C.dark }}>
        <Stars count={80} />
        <div className="max-w-7xl mx-auto relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div className="space-y-4 sm:space-y-5">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight">Our Competitive Advantage</h2>
            <h3 className="text-orange-400 font-bold text-base sm:text-lg">Why Choose ISF Athena?</h3>
            <div className="space-y-3">
              {[
                'Blended learning design principles and adult learning best practices',
                'Modular, scalable structure for easy adaptation',
                'Extensive customization options for brand alignment',
                'Built-in analytics supporting measurable outcomes',
                'Versatile solutions for both schools and enterprises',
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-orange-500 rounded-full flex-shrink-0 mt-2" />
                  <p className="text-gray-300 text-xs sm:text-sm leading-relaxed">{item}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="flex flex-row sm:flex-row lg:flex-col gap-6 items-center justify-center">
            {[
              { stat: '100%', title: 'Quality Assured', desc: 'Every module peer-reviewed by subject matter experts' },
              { stat: '360°', title: 'Complete Solution', desc: 'End-to-end courseware with all necessary components' },
            ].map((item, idx) => (
              <div key={idx} className="relative w-36 h-36 sm:w-44 sm:h-44 flex-shrink-0">
                <svg className="w-full h-full -rotate-90" viewBox="0 0 176 176">
                  <circle cx="88" cy="88" r="76" stroke="#ea580c" strokeWidth="8" fill="none" opacity="0.3" />
                  <circle cx="88" cy="88" r="76" stroke="#ea580c" strokeWidth="8" fill="none" strokeDasharray="478" strokeDashoffset="0" strokeLinecap="round" />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-2">
                  <span className="text-2xl sm:text-3xl font-bold text-white">{item.stat}</span>
                  <span className="text-orange-400 font-bold text-xs mt-1">{item.title}</span>
                  <span className="text-gray-400 text-xs mt-1 leading-tight">{item.desc}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          14. K-12 PROGRAMS
      ══════════════════════════════════════════════════════════ */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-16 sm:py-20 overflow-hidden" style={{ backgroundColor: C.mid }}>
        <Stars count={60} />
        <div className="max-w-7xl mx-auto relative z-10">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3">K-12 Education Programs</h2>
          <p className="text-gray-400 text-sm sm:text-base mb-8 sm:mb-12">Comprehensive curriculum coverage from kindergarten through senior secondary, aligned with national and international standards.</p>
          <div className="space-y-4 sm:space-y-5">
            {[
              { icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342M6.75 15a.75.75 0 100-1.5.75.75 0 000 1.5zm0 0v-3.675A55.378 55.378 0 0112 8.443m-7.007 11.55A5.981 5.981 0 006.75 15.75v-1.5" /></svg>, title: 'Kindergarten (K1-K2)', desc: 'Literacy, numeracy, motor skills, and play-based learning foundations' },
              { icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" /></svg>, title: 'Primary (Grades 1-5)', desc: 'English, Math, EVS, Art, and Life Skills development' },
              { icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>, title: 'Upper Primary (6-8)', desc: 'Science, Math, Social Studies, and Languages' },
              { icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19.114 5.636a9 9 0 010 12.728M16.463 8.288a5.25 5.25 0 010 7.424M6.75 8.25l4.72-4.72a.75.75 0 011.28.53v15.88a.75.75 0 01-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.01 9.01 0 012.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75z" /></svg>, title: 'Secondary (9-10)', desc: 'STEM, Humanities, and Commerce basics' },
              { icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M12 14l9-5-9-5-9 5 9 5z" /><path d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0112 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" /></svg>, title: 'Senior Secondary (11-12)', desc: 'Science, Commerce, and Arts streams with specializations' },
            ].map((item, idx, arr) => (
              <div key={idx} className="flex items-start gap-4 sm:gap-5">
                <div className="relative flex-shrink-0">
                  <div className="w-10 h-10 sm:w-11 sm:h-11 border-2 border-orange-500 rounded-full flex items-center justify-center text-orange-500">{item.icon}</div>
                  {idx < arr.length - 1 && <div className="absolute left-1/2 top-10 sm:top-11 w-px bg-gray-700 -translate-x-1/2" style={{ height: '24px' }} />}
                </div>
                <div className="pb-3">
                  <h3 className="text-orange-400 font-bold text-sm sm:text-base mb-1">{item.title}</h3>
                  <p className="text-gray-300 text-xs sm:text-sm">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          15. TECHNOLOGY & ENGINEERING
      ══════════════════════════════════════════════════════════ */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-16 sm:py-20 overflow-hidden" style={{ backgroundColor: C.dark }}>
        <Stars count={80} />
        <div className="max-w-7xl mx-auto relative z-10">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-8 sm:mb-10">Technology & Engineering Programs</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 mb-4 sm:mb-5">
            {[
              { title: 'Artificial Intelligence', desc: 'ML, DL, NLP, Computer Vision, Agentic AI' },
              { title: 'Information Technology', desc: 'Programming, Networking, Cybersecurity' },
              { title: 'Data Science', desc: 'Data Analytics, Big Data, Visualization' },
              { title: 'Emerging Technologies', desc: 'IoT, Blockchain, Quantum Computing, Robotics, VLSI' },
            ].map((item, idx) => (
              <div key={idx} className="border border-orange-500/70 rounded-2xl p-5 sm:p-6" style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}>
                <h3 className="text-orange-400 font-bold text-sm sm:text-base mb-2">{item.title}</h3>
                <p className="text-gray-300 text-xs sm:text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5">
            {[
              { icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4.5 12a7.5 7.5 0 0015 0m-15 0a7.5 7.5 0 1115 0m-15 0H3m16.5 0H21m-1.5 0H12m-8.457 3.077l1.41-.513m14.095-5.13l1.41-.513M5.106 17.785l1.15-.964m11.49-9.642l1.149-.964M7.501 19.795l.75-1.3M15.75 4.505l-.75 1.3M11.25 21v-1.5m0-16.5V1.5" /></svg>, title: 'Mechanical Engineering', desc: 'Design, Manufacturing, Thermodynamics' },
              { icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M8.25 3v1.5M4.5 8.25H3m18 0h-1.5M4.5 12H3m18 0h-1.5m-15 3.75H3m18 0h-1.5M8.25 19.5V21M12 3v1.5m0 15V21m3.75-18v1.5m0 15V21m-9-1.5h10.5a2.25 2.25 0 002.25-2.25V6.75a2.25 2.25 0 00-2.25-2.25H6.75A2.25 2.25 0 004.5 6.75v10.5a2.25 2.25 0 002.25 2.25zm.75-12h9v9h-9v-9z" /></svg>, title: 'Electronics & Communication', desc: 'Microprocessors, Signal Processing' },
              { icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25m18 0A2.25 2.25 0 0018.75 3H5.25A2.25 2.25 0 003 5.25m18 0H3" /></svg>, title: 'Computer Science', desc: 'Programming, AI, Networks, Databases' },
            ].map((item, idx) => (
              <div key={idx} className="border border-orange-500/70 rounded-2xl p-5 sm:p-6" style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}>
                <div className="text-orange-500 mb-3">{item.icon}</div>
                <h3 className="text-orange-400 font-bold text-sm sm:text-base mb-2">{item.title}</h3>
                <p className="text-gray-300 text-xs sm:text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          16. VOCATIONAL & INDUSTRIAL
      ══════════════════════════════════════════════════════════ */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-16 sm:py-20 overflow-hidden" style={{ backgroundColor: C.mid }}>
        <Stars count={60} />
        <div className="max-w-7xl mx-auto relative z-10">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3">Vocational & Industrial Training</h2>
          <p className="text-gray-400 text-sm sm:text-base mb-8 sm:mb-12">Comprehensive skill development programs across multiple trades and industries, designed to enhance employability and entrepreneurship.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            {[
              { title: 'Technical Trades', items: ['Automotive Mechanic', 'Electric Vehicle Maintenance', 'Electrical Wiring & Repair', 'Plumbing & Masonry', 'Welding & Carpentry'] },
              { title: 'Service Industries', items: ['Hospitality & Tourism', 'Front Office Operations', 'Food Production', 'Healthcare Support', 'Nursing Aid & Lab Tech'] },
              { title: 'Creative & Digital', items: ['Textile & Fashion Design', 'Tailoring & Embroidery', 'Digital Marketing', 'Office Automation', 'BPO Services'] },
            ].map((col, idx) => (
              <div key={idx}>
                <h3 className="text-orange-400 font-bold text-base sm:text-lg mb-4 sm:mb-5">{col.title}</h3>
                <div className="space-y-2 sm:space-y-3">
                  {col.items.map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-orange-500 rounded-full flex-shrink-0" />
                      <p className="text-gray-300 text-xs sm:text-sm">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          17. HEALTHCARE & MEDICAL
      ══════════════════════════════════════════════════════════ */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-16 sm:py-20 overflow-hidden" style={{ backgroundColor: C.dark }}>
        <Stars count={60} />
        <div className="max-w-7xl mx-auto relative z-10">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-8 sm:mb-10">Healthcare & Medical Education</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8">
            {[
              { icon: <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25m18 0A2.25 2.25 0 0018.75 3H5.25A2.25 2.25 0 003 5.25m18 0H3" /></svg>, title: 'MBBS Curriculum', desc: 'Comprehensive medical education covering Anatomy, Physiology, Pharmacology, and Community Medicine' },
              { icon: <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21" /></svg>, title: 'Allied Health', desc: 'Physiotherapy, Pharmacy, Radiology, and Lab Technology programs' },
              { icon: <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" /></svg>, title: 'Nursing Programs', desc: 'General, Pediatric, Psychiatric, and Community nursing specializations' },
              { icon: <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23-.693L5 14.5m14.8.8l1.402 1.402c1 1 .03 2.798-1.338 2.798a1.875 1.875 0 01-1.324-.549l-1.942-1.942" /></svg>, title: 'Medical Research', desc: 'Genomics, Bioinformatics, and Biotechnology research programs' },
            ].map((item, idx) => (
              <div key={idx} className="flex items-start gap-4 sm:gap-5">
                <div className="text-orange-500 flex-shrink-0">{item.icon}</div>
                <div>
                  <h3 className="text-orange-400 font-bold text-sm sm:text-base mb-1 sm:mb-2">{item.title}</h3>
                  <p className="text-gray-300 text-xs sm:text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          18. MANAGEMENT & SPECIALIZED DOMAINS
      ══════════════════════════════════════════════════════════ */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-16 sm:py-20 overflow-hidden" style={{ backgroundColor: C.mid }}>
        <Stars count={60} />
        <div className="max-w-7xl mx-auto relative z-10 grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-12 items-start">
          <div className="space-y-6">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight">Management & Specialized Domains</h2>
            <div>
              <h3 className="text-white font-bold text-sm sm:text-base mb-3 sm:mb-4">Business & Management</h3>
              <div className="space-y-3 sm:space-y-4">
                {[
                  { title: 'Business Administration', sub: 'Marketing, Finance, HR, Operations' },
                  { title: 'Strategic Management', sub: 'Leadership, Analytics, Project Management' },
                  { title: 'International Business', sub: 'Trade, Logistics, Supply Chain' },
                  { title: 'Entrepreneurship', sub: 'Startup Planning, Innovation, Franchise Models' },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-orange-500 rounded-full flex-shrink-0 mt-1.5" />
                    <div>
                      <p className="text-white font-semibold text-xs sm:text-sm">{item.title}</p>
                      <p className="text-gray-400 text-xs mt-0.5">{item.sub}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="lg:pt-24">
            <h3 className="text-white font-bold text-sm sm:text-base mb-3 sm:mb-4">Specialized Fields</h3>
            <div className="space-y-3 sm:space-y-4">
              {[
                { title: 'Agriculture & Allied Sciences', sub: 'Agronomy, Horticulture, Animal Husbandry' },
                { title: 'Architecture & Design', sub: 'Urban Planning, Interior Design, Sustainable Design' },
                { title: 'Hospitality Management', sub: 'Hotel Operations, Culinary Arts, Event Management' },
                { title: 'Environmental Studies', sub: 'Ecology, Climate Change, Sustainability' },
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-orange-500 rounded-full flex-shrink-0 mt-1.5" />
                  <div>
                    <p className="text-white font-semibold text-xs sm:text-sm">{item.title}</p>
                    <p className="text-gray-400 text-xs mt-0.5">{item.sub}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden w-full" style={{ minHeight: '360px', height: '460px' }}>
            <img src="/Assests/home_page_159.png" alt="Management & Specialized Domains" className="w-full h-full object-cover rounded-2xl" />
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          19. ANGANWADI & COMMUNITY PROGRAMS
      ══════════════════════════════════════════════════════════ */}
      <section
        className="relative px-4 sm:px-8 lg:px-24 py-16 sm:py-20 overflow-hidden"
        style={{ backgroundImage: "url('/Assests/home_page_160.png')", backgroundSize: 'cover', backgroundPosition: 'center' }}
      >
        <div className="absolute inset-0 z-0" style={{ backgroundColor: 'rgba(30,10,0,0.70)' }} />
        <div className="max-w-4xl mx-auto relative z-10">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3">Anganwadi & Community Programs</h2>
          <p className="text-gray-200 text-xs sm:text-sm mb-6 sm:mb-8 max-w-3xl leading-relaxed">
            Specialized training programs for community health workers and early childhood development professionals, supporting government initiatives and grassroots education.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { title: 'Training Programs', desc: 'Induction, Job Training, and Refresher Training for Anganwadi workers' },
              { title: 'Online Modules', desc: 'MWCD Courses, UNICEF POSHAN, NIPCCD e-Learning platforms' },
              { title: 'Mobile Applications', desc: 'ICDS-CAS, Poshan Tracker, e-Samvad for field workers' },
              { title: 'Community Training', desc: 'Vocational skills, sustainability, women empowerment programs' },
            ].map((item, idx) => (
              <div key={idx} className="rounded-xl p-5 sm:p-6" style={{ backgroundColor: 'rgba(20,8,0,0.78)', border: '1px solid rgba(200,100,20,0.5)' }}>
                <h3 className="text-white font-bold text-sm sm:text-base mb-2">{item.title}</h3>
                <p className="text-gray-300 text-xs sm:text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          20. GET STARTED  (Redesigned + fully responsive)
      ══════════════════════════════════════════════════════════ */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-20 sm:py-24 overflow-hidden" style={{ backgroundColor: C.mid }}>
        <Stars count={80} />
        {/* Ambient glow */}
        <div className="absolute top-0 left-1/4 w-64 sm:w-96 h-64 sm:h-96 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle,rgba(234,88,12,0.13) 0%,transparent 70%)' }} />
        <div className="absolute bottom-0 right-1/4 w-56 sm:w-80 h-56 sm:h-80 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle,rgba(234,88,12,0.08) 0%,transparent 70%)' }} />

        <div className="max-w-5xl mx-auto relative z-10">

          {/* Header */}
          <div className="text-center mb-10 sm:mb-14">
            <span className="inline-block px-4 py-1 rounded-full text-xs font-bold tracking-widest text-orange-400 border border-orange-500/40 mb-4" style={{ backgroundColor: 'rgba(234,88,12,0.1)' }}>
              START YOUR JOURNEY
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-3 sm:mb-4">Get Started with ISF Athena</h2>
            <p className="text-gray-400 text-sm sm:text-base max-w-xl mx-auto leading-relaxed">
              Five simple steps to launch world-class courseware for your organization
            </p>
          </div>

          {/* Step Cards — 3 cols on md+, 1 col on mobile */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5 mb-4 sm:mb-5">
            <StepCard num="01" icon={<PhoneIcon />} title="Schedule Discovery Call" desc="Meet with our Courseware Team to discuss your needs and goals" />
            <StepCard num="02" icon={<DocIcon />}   title="Complete Brief"          desc="Outline learning needs, target audience, and expected outcomes" />
            <StepCard num="03" icon={<ClipIcon />}  title="Receive Proposal"        desc="Get detailed scope, modules, delivery mode, timeline, and pricing" />
          </div>

          {/* Step Cards — 2 cols on sm+, 1 col on mobile */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 mb-10 sm:mb-12">
            <StepCard num="04" icon={<PenIcon />}    title="Sign Agreement" desc="Finalize production timeline and customization requirements" />
            <StepCard num="05" icon={<RocketIcon />} title="Begin Rollout"   desc="Monitor learner engagement, collect feedback, and measure outcomes" />
          </div>

          {/* CTA Buttons — stacked on mobile, row on sm+ */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 sm:gap-5 justify-center">
            <button
              className="group inline-flex items-center justify-center gap-3 px-7 sm:px-8 py-4 text-white font-bold rounded-xl text-sm sm:text-base transition-all duration-300 hover:scale-105 hover:shadow-2xl w-full sm:w-auto"
              style={{ background: 'linear-gradient(135deg,#ea580c,#c2410c)', boxShadow: '0 8px 32px rgba(234,88,12,0.35)' }}
            >
              <PhoneIcon />
              Contact Us Today
              <ArrowIcon />
            </button>
            <button
              className="group inline-flex items-center justify-center gap-3 px-7 sm:px-8 py-4 font-bold rounded-xl text-sm sm:text-base transition-all duration-300 hover:scale-105 w-full sm:w-auto"
              style={{ border: '2px solid rgba(234,88,12,0.7)', color: '#fb923c', backgroundColor: 'rgba(234,88,12,0.08)' }}
            >
              <ChatIcon />
              View Sample Modules
            </button>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          FOOTER
      ══════════════════════════════════════════════════════════ */}
      {/* <footer
        className="relative px-4 sm:px-8 lg:px-24 py-10 sm:py-12 border-t"
        style={{ backgroundColor: C.deep, borderColor: 'rgba(234,88,12,0.2)' }}
      >
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
          <div>
            <p className="text-white font-bold text-lg sm:text-xl">ISF Athena</p>
            <p className="text-gray-400 text-xs sm:text-sm mt-1">Empowering learners through structured, scalable education</p>
          </div>
          <p className="text-gray-600 text-xs sm:text-sm">© 2025 ISF Athena. All rights reserved.</p>
        </div>
      </footer> */}

    </div>
  );
}